export interface DiscoverSummary {
  businesses: number;
  decision_makers: number;
  emails: number;
}

export interface BestSearch {
  search_query: string;
  city_name: string;
  state_name: string;
  businesses: number;
  decision_makers: number;
  emails: number;
}

export interface WebsiteScrapedData {
  data_type: string;
  data: string;
  created_at: string;
}

export interface EmailData {
  id?: number; // Email ID for verification
  email: string;
  primary_reason: string;
  positive_signals: string[];
  people_count: number;
  people_roles: string[];
  people_display: string;
  created_at: string;
  context?: string;
  sources?: string[];
  is_guessed?: boolean;
  // New fields for email verification pipeline
  quality?: string; // Legacy field (safe/likely/unconfirmed)
  verification_status?: 'DISCOVERED' | 'PENDING' | 'GUARANTEED' | 'INVALID' | 'RISKY' | 'INBOX_FULL' | 'GENERIC' | 'UNKNOWN';
  confidence_level?: 'HIGH' | 'MEDIUM' | 'LOW';
  confidence_score?: number;
  source_type?:
    | 'GOVERNMENT'
    | 'BBB'
    | 'CHAMBER'
    | 'INDUSTRY_ASSOCIATION'
    | 'NEWS'
    | 'COMPANY_WEBSITE'
    | 'CONTACT_PAGE'
    | 'OTHER_WEBSITE'
    | 'PATTERN_MATCH'
    | 'GENERIC';
  source_url?: string;
  verified_at?: string;
  ai_score?: number;
  is_catch_all?: boolean; // Domain is catch-all (based on user's verification history)
  is_role_account?: boolean;
  source?: 'person' | 'place_data'; // DB table origin: PersonData vs PlaceWebsiteScrapeData
}

export function formatEmailSources(sources?: string[]): string {
  if (!sources || sources.length === 0) return '';
  
  const sourceMap: Record<string, string> = {
    'GOOGLE': 'Google',
    'DUCKDUCKGO': 'DuckDuckGo',
    'COMPANY_WEBSITE': 'Company website',
    'OTHER_WEBSITE': 'Other website',
  };
  
  const formatted = sources
    .map(s => sourceMap[s] || s)
    .filter((v, i, arr) => arr.indexOf(v) === i);
  
  return formatted.length > 0 ? `(${formatted.join(', ')})` : '';
}

export interface PersonLocation {
  place_id: number;
  name?: string | null;
  city?: string | null;
}

export interface Person {
  id: number;
  first_name: string;
  last_name?: string;
  role: string;
  score: number;
  ai_score?: number;
  corrected_first_name?: string;
  corrected_last_name?: string;
  corrected_role?: string;
  source_url?: string;
  emails?: EmailData[];
  is_unlocked?: boolean;
  other_locations?: PersonLocation[];
  email_access_state?:
    | 'auto_found'
    | 'auto_no_email'
    | 'finding'
    | 'not_searched'
    | 'gated_upgrade'
    | 'manual_unlocked'
    | 'no_email';
  email_access_source?: string | null;
  unlock_type?:
    | 'FULL_PRICE'
    | 'CATCH_ALL'
    | 'FREE_NO_EMAIL'
    | 'FAILED_INVALID_PERSON'
    | 'FAILED_NO_WEBSITE'
    | 'FAILED_INVALID_DOMAIN'
    | 'FAILED_NO_PATTERNS'
    | 'FAILED_INSUFFICIENT_BALANCE'
    | 'FAILED_UNKNOWN';
}

export interface PlaceReview {
  id: number;
  reviewer_name?: string | null;
  rating?: number | null;
  relative_time?: string | null;
  text?: string | null;
  owner_response_text?: string | null;
  owner_response_relative_time?: string | null;
}

export interface LeadSignals {
  has_website?: boolean | null;
  has_phone?: boolean | null;
  has_reviews?: boolean | null;
  review_strength?: 'none' | 'light' | 'established' | string | null;
  has_ssl?: boolean | null;
  is_mobile_ready?: boolean | null;
  has_click_to_call?: boolean | null;
  detected_pixels?: string[] | null;
  google_ads_signal?: GoogleAdsSignal | null;
  visible_reviews_scraped_count?: number | null;
  visible_latest_review_age_days?: number | null;
  visible_owner_response_rate?: number | null;
  visible_review_photo_count?: number | null;
  visible_high_rating_review_count?: number | null;
  visible_low_rating_review_count?: number | null;
}

export interface GoogleAdsSignal {
  status: 'detected' | 'not_detected' | 'inconclusive';
  freshness: 'fresh' | 'stale' | 'unknown';
  last_checked_at?: string | null;
  last_detected_at?: string | null;
  last_attempted_at?: string | null;
  pages_checked: number;
  pages_detected: number;
  detected_page_paths: string[];
  evidence_types: string[];
  inconclusive_reason?: string | null;
}

export interface WebsiteFitEvidence {
  tag: string;
  matched_term: string;
  location: string;
  page_url: string;
  snippet: string;
}

export interface WebsiteFit {
  verdict: 'match' | 'partial' | 'unknown' | 'weak';
  label: string;
  delivery_group: 'website_confirmed' | 'additional';
  fit_score?: number | null;
  matched_offerings: string[];
  matched_customers: string[];
  evidence: WebsiteFitEvidence[];
  pages_analyzed: number;
}

export interface Place {
  id: number;
  name: string;
  address: string;
  house_number?: string;
  road?: string;
  postcode?: string;
  city?: string;
  country?: string;
  phone_number: string;
  phone_numbers?: string[];
  website: string;
  review_count: number;
  review_rating: number;
  /** Drawer-only (from PlaceDetail) — absent on the /results list row. */
  introduction?: string;
  /** Drawer-only (from PlaceDetail) — absent on the /results list row. */
  book_visit?: string;
  /** Drawer-only (from PlaceDetail) — absent on the /results list row. */
  menu?: string;
  search_id: number;
  task_id: number;
  /** Drawer-only (from PlaceDetail) — absent on the /results list row. */
  industries?: Industry[];
  main_industry?: string;
  website_status?: string;
  website_scraped_data?: WebsiteScrapedData[];
  company_emails?: EmailData[];
  people?: Person[];
  emails_summary?: {
    total: number;
    by_confidence: {
      high: number;
      medium: number;
      low: number;
      verified: number;
    };
    by_type: {
      personal: number;
      role: number;
    };
    verifiable_by_confidence: {
      high: number;
      medium: number;
      low: number;
    };
    verification_eligible: boolean;
  };
  is_previously_seen?: boolean;
  email_action_gated?: boolean;
  email_gate_reason?: string | null;
  has_ssl?: boolean | null;
  is_mobile_ready?: boolean | null;
  has_click_to_call?: boolean | null;
  detected_pixels?: string[] | null;
  lead_signals?: LeadSignals | null;
  website_fit?: WebsiteFit | null;
}

export interface Industry {
  id: number;
  name: string;
}

/**
 * Drawer-only fields, fetched lazily via getPlaceDetail() when the drawer
 * opens. The /results row (Place, above) omits these — see the backend's
 * PlaceResponse/PlaceDetailResponse split in app/schemas/results.py.
 */
export interface PlaceDetail {
  id: number;
  house_number?: string;
  road?: string;
  postcode?: string;
  city?: string;
  country?: string;
  introduction?: string;
  book_visit?: string;
  menu?: string;
  temporary_closed?: boolean;
  average_money_spent?: string;
  lgbtq_friendly?: boolean;
  industries: Industry[];
  website_scraped_data: WebsiteScrapedData[];
  lead_signals?: LeadSignals | null;
  website_fit?: WebsiteFit | null;
}

export interface ResultsFilter {
  searchTerm?: string;
  industry?: string;
  minRating?: number;
  maxRating?: number;
  minReviews?: number;
  maxReviews?: number;
  hasWebsite?: boolean | null;
  hasPhone?: boolean | null;
  hasBookVisit?: boolean | null;
  hasDecisionMakers?: boolean | null;
  hasEmail?: boolean | null;
  emailVerificationStatus?: string[];
  strongMatchesOnly?: boolean;
  matchedOffering?: string;
  googleAdsSignal?: 'detected_recently' | 'no_ad_tracking';
}

export interface SearchStatistics {
  min_rating: number;
  max_rating: number;
  min_reviews: number;
  max_reviews: number;
}

export interface FeatureAccess {
  confidence_badges: boolean;
  email_verification: boolean;
  dm_unlock: boolean;
  reviews: boolean;
}

export interface ResultsSummary {
  total_verified: number;
  total_invalid: number;
  total_actionable: number;
  total_generic_skipped: number;
  total_guaranteed: number;
  dm_candidates: number;
  dm_business_count: number;
  dm_unlocked: number;
  missing_owners: number;
  has_any_data: boolean;
  ready_persons_count: number;
  ready_personal_count: number;
  ready_company_count: number;
  website_confirmed_count: number;
  additional_category_count: number;
  google_ads_detected_count?: number | null;
}

export const EMPTY_RESULTS_SUMMARY: ResultsSummary = {
  total_verified: 0,
  total_invalid: 0,
  total_actionable: 0,
  total_generic_skipped: 0,
  total_guaranteed: 0,
  dm_candidates: 0,
  dm_business_count: 0,
  dm_unlocked: 0,
  missing_owners: 0,
  has_any_data: false,
  ready_persons_count: 0,
  ready_personal_count: 0,
  ready_company_count: 0,
  website_confirmed_count: 0,
  additional_category_count: 0,
};

export interface ResultsState {
  places: Place[];
  summary: ResultsSummary | null;
  pagination: {
    page: number;
    totalPages: number;
    itemsPerPage: number;
    totalItems: number;
    places_locked?: number;
    total_places_found?: number;
    credits_charged?: number;
    has_locked_results?: boolean;
    all_locked?: boolean;
    results_capped?: boolean;
    total_links_found?: number;
    visible_results_cap?: number | null;
  };
  filters: ResultsFilter;
  statistics: SearchStatistics | null;
  verifiable_emails_count: number;
  enrichable_people_count: number;
  feature_access: FeatureAccess | null;
  current_plan: string | null;
  auto_find_status?: string | null;
  auto_find_mode?: string | null;
  auto_find_success_limit?: number;
  auto_find_success_count?: number;
  auto_find_chargeable_count?: number;
  auto_find_attempt_count?: number;
  auto_find_no_email_count?: number;
  auto_find_catch_all_count?: number;
  auto_find_guardrail_reason?: string | null;
  auto_find_business_target?: number;
  auto_find_business_attempt_count?: number;
  auto_find_max_people_per_business?: number;
  auto_find_monthly_pool_limit?: number;
  auto_find_monthly_pool_used?: number;
  loading: boolean;
  error: string | null;
}

export interface PersonUnlockRequest {
  search_id?: number;
}

export interface PersonUnlockResponse {
  success: boolean;
  person_id: number;
  job_token: string; // Legacy - use batch_id for polling
  batch_id?: string; // New - for batch polling

  queued?: boolean;
  processing?: boolean;
  message?: string;

  email?: string;
  verification_status?: string;
  unlock_type?: string;
  is_catch_all?: boolean;
  pattern_used?: string;
  dm_unlocks_charged?: number;
  remaining_dm_unlocks: number;
  error?: string;
}

export interface JobStatusResponse {
  status: string;
}

export interface BulkUnlockRequest {
  person_ids?: number[];
  search_id?: number;
  bulk?: boolean;
  name?: string;
  min_rating?: number;
  max_rating?: number;
  min_reviews?: number;
  max_reviews?: number;
  has_website?: boolean;
  has_phone?: boolean;
  has_book_visit?: boolean;
  has_decision_makers?: boolean;
  has_email?: boolean;
  email_verification_status?: string;
  industries?: string;
  delivery_group?: 'website_confirmed' | 'additional';
  matched_offering?: string;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking';
}

export interface UnlockEstimateRequest {
  person_ids?: number[];
  bulk?: boolean;
  search_id?: number;
  name?: string;
  min_rating?: number;
  max_rating?: number;
  min_reviews?: number;
  max_reviews?: number;
  has_website?: boolean;
  has_phone?: boolean;
  has_book_visit?: boolean;
  has_decision_makers?: boolean;
  has_email?: boolean;
  email_verification_status?: string;
  industries?: string;
  delivery_group?: 'website_confirmed' | 'additional';
  matched_offering?: string;
  google_ads_signal?: 'detected_recently' | 'no_ad_tracking';
}

export interface BulkUnlockResponse {
  batch_id: string; // UUID for polling
  total: number;
  queued: number;
  results: any[];
  message?: string;
}

export interface FoundContact {
  name: string;
  role: string;
  email: string;
}

export interface BatchStatusResponse {
  batch_id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED';
  progress_percent: number; // 0-100
  total: number;
  succeeded: number; // Emails found (COMPLETED with email)
  failed: number; // No email found or error
  pending: number;
  processing: number;
  found_contacts?: FoundContact[];
}

export interface ActiveBatchResponse {
  has_active: boolean;
  batch_id?: string;
  search_id?: number;
  total?: number;
  completed?: number;
  progress_percent?: number;
  started_at?: string;
}

export interface PlaceEstimate {
  place_id: number;
  place_name: string;
  dm_count: number;
  person_ids: number[];
  people: PersonEstimatePreview[];
}

export interface PersonEstimatePreview {
  id: number;
  group_id?: number | null;
  first_name: string;
  last_name?: string | null;
  role: string;
  corrected_first_name?: string | null;
  corrected_last_name?: string | null;
  corrected_role?: string | null;
}

export interface UnlockEstimateResponse {
  already_unlocked: number;
  already_has_email: number;
  needs_discovery: number;
  min_cost: number;
  max_cost: number;
  estimated_cost: number;
  estimated_success_rate: number;
  places: PlaceEstimate[];
} 
